/**
 * PrintingFillamentTags Component
 *
 * Fetches and displays filament tags for a printing.
 * Reads tag, material, and color directly from the Tag rows returned by
 * getList('tags', { filter: { printingId } }) — no second fetch needed.
 *
 * Mimics hardware-digester behavior with React Admin 5.x and Material-UI v5.
 */

import { useState, useEffect } from 'react';
import { useDataProvider, useRecordContext } from 'react-admin';
import { InputLabel, LinearProgress } from '@mui/material';
import capitalize from 'lodash/capitalize';
import { Printing, Tag } from '../../types';

interface TagRow {
  tag: string;
  material: string;
  color: string;
}

export const PrintingFillamentTags = () => {
  const record = useRecordContext<Printing>();
  const printingId = record?.id;
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [rows, setRows] = useState<TagRow[] | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (!printingId) {
      setLoading(false);
      return;
    }

    dataProvider
      .getList<Tag>('tags', {
        pagination: { page: 1, perPage: 999 },
        sort: { field: 'createdAt', order: 'ASC' },
        filter: { printingId },
      })
      .then(({ data }) => {
        if (!isMounted) return;
        setRows(
          data.map((t) => ({
            tag: t.tag,
            material: t.material ?? 'N/A',
            color: t.color ?? 'N/A',
          }))
        );
        setLoading(false);
      })
      .catch((err: Error) => {
        if (!isMounted) return;
        // eslint-disable-next-line no-console
        console.error(err);
        setLoading(false);
        setError(true);
      });

    return () => {
      isMounted = false;
    };
  }, [dataProvider, printingId]);

  if (error) {
    return (
      <>
        <InputLabel shrink={true} sx={{ margin: '8px 0 4px 0' }}>
          <span>Fillament Tags</span>
        </InputLabel>
        <span>Sorry, an error occurred.</span>
      </>
    );
  }

  return (
    <>
      <InputLabel shrink={true} sx={{ margin: '8px 0 4px 0' }}>
        <span>Fillament Tags</span>
      </InputLabel>
      {loading ? (
        <div style={{ width: 60 }}>
          <LinearProgress />
        </div>
      ) : !rows?.length ? (
        <span>{'N/A'}</span>
      ) : (
        rows.map(({ tag, material, color }) => (
          <p key={tag} style={{ margin: 0 }}>
            {tag} - {material} - {capitalize(color)}
          </p>
        ))
      )}
    </>
  );
};
